import { AfterViewInit, Component, ElementRef, HostListener, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Store } from '@ngxs/store';
import * as Sentry from '@sentry/angular-ivy';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SegmentService } from 'src/app/services/segment/segment.service';
import { AuthState } from 'src/app/store/auth/auth.state';
import { environment } from 'src/environments/environment';
type FormFieldElement = HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement;

export interface HubspotFormDialogData {
  title?: string;
  formConfig?: {
    portalId?: string;
    formId?: string;
    region?: string;
    target?: string;
    selectCheckbox?: string;
    addonType?: string;
    /** Value for Integration Requested field (e.g. integration name from marketplace) */
    integrationRequest?: string;
    [key: string]: any;
  };
  [key: string]: any;
}
@Component({
  selector: 'app-hubspot-form-dialog',
  templateUrl: './hubspot-form-dialog.component.html',
  styleUrls: ['./hubspot-form-dialog.component.scss'],
})
export class HubspotFormDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  private formScriptLoaded = false;
  private formContainer: HTMLElement | null = null;
  private formInstance: any = null;
  private destroy$ = new Subject<void>();
  private scriptElement: HTMLScriptElement | null = null;
  private formIframe: HTMLIFrameElement | null = null;
  private isTouchDragging = false;
  private formReadyTriggered = false;
  private isRetrying = false;
  private readonly originalFormId: string | null = null;
  private resizeObserver: ResizeObserver | null = null;
  private scrollTimeout: any = null;
  private dialogContent: HTMLElement | null = null;
  private boundHandleDialogScroll: any = null;
  private loadingTimeout: any = null;
  private readonly formLoadTimeout = 15000; // 15 seconds timeout
  public isLoading = true;
  public loadingStartTime = 0;
  public loadingTimeElapsed = 0;
  public showTimeoutMessage = false;
  formConfig: {
    portalId: string;
    formId: string;
    region: string;
    target: string;
    selectCheckbox?: string;
    addonType?: string;
    integrationRequest?: string;
    [key: string]: any;
  } = {
    portalId: '',
    formId: '',
    region: 'na1',
    target: '#hubspot-form-container',
  };
  constructor(
    public dialogRef: MatDialogRef<HubspotFormDialogComponent>,
    private renderer: Renderer2,
    private elementRef: ElementRef,
    private store: Store,
    private segmentService: SegmentService,
    @Inject(MAT_DIALOG_DATA) public data: HubspotFormDialogData,
  ) {
    if (data?.formConfig) {
      this.originalFormId = data.formConfig.formId || null;
      this.formConfig = { ...this.formConfig, ...data.formConfig };
    }

    // Ensure defaults from environment if not provided by caller
    this.formConfig.portalId = this.formConfig.portalId || environment.hubspotPortalId;
    this.formConfig.formId = this.formConfig.formId || environment.hubspotFormId;
    this.formConfig.region = this.formConfig.region || environment.hubspotRegion || 'na1';

    // Check if HubSpot script is already loaded
    this.formScriptLoaded = !!document.querySelector('script[src*="js.hsforms.net/forms/embed/v2.js"]') &&
                           typeof window.hbspt !== 'undefined' &&
                           typeof window.hbspt.forms !== 'undefined';
  }
  ngOnInit(): void {
    this.dialogRef.backdropClick()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.closeDialog();
      });
  }
  ngAfterViewInit(): void {
    this.createForm();

    this.formContainer = this.elementRef.nativeElement.querySelector('#hubspot-form-container');
    if (!this.formContainer) {
      this.handleError('Form container not found');
      return;
    }
    this.dialogContent = this.elementRef.nativeElement.querySelector('.dialog-content');
    if (this.dialogContent) {
      this.boundHandleDialogScroll = this.handleDialogScroll.bind(this);
      this.dialogContent.addEventListener('scroll', this.boundHandleDialogScroll);
    }

    this.isLoading = true;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    if (this.scriptElement && document.body.contains(this.scriptElement)) {
      this.renderer.removeChild(document.body, this.scriptElement);
    }

    if (this.dialogContent && this.boundHandleDialogScroll) {
      this.dialogContent.removeEventListener('scroll', this.boundHandleDialogScroll);
      this.boundHandleDialogScroll = null;
    }
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = null;
    }
  }
  private handleDialogScroll(): void {
    if (!this.dialogContent) {
      return;
    }
    this.dialogContent.classList.add('is-scrolling');
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    this.scrollTimeout = setTimeout(() => {
      if (this.dialogContent) {
        this.dialogContent.classList.remove('is-scrolling');
      }
      this.scrollTimeout = null;
    }, 100);
  }

  /**
   * Creates the HubSpot form with performance monitoring
   */
  private createForm(): void {
    // Initialize loading start time
    this.loadingStartTime = performance.now();
    this.isLoading = true;
    this.showTimeoutMessage = false;

    // Set up timeout for form loading
    this.setupLoadingTimeout();

    const createStartTime = performance.now();
    if (!this.formScriptLoaded) {
      this.handleError('HubSpot form script not loaded properly');
      return;
    }
    if (!window.hbspt || !window.hbspt.forms) {
      this.handleError('HubSpot forms API not available');
      return;
    }
    try {
      Sentry.addBreadcrumb({
        category: 'hubspot',
        message: 'Creating HubSpot form',
        level: 'info',
        data: {
          formId: this.formConfig.formId,
          isRetry: this.isRetrying,
          timeFromStart: (performance.now() - this.loadingStartTime).toFixed(2) + 'ms',
        },
      });
      const selectCheckbox = this.formConfig.selectCheckbox;
      const addonType = this.formConfig.addonType;
      const user = this.store.selectSnapshot(AuthState.user);
      const company = this.store.selectSnapshot(AuthState.company);
      const fields: Record<string, string> = {};
      if (user) {
        if (user.name) {
          const nameParts = user.name.split(' ');
          if (nameParts.length > 1) {
            fields.firstname = nameParts[0];
            fields.lastname = nameParts.slice(1).join(' ');
          } else {
            fields.firstname = user.name;
          }
        }
        if (user.email) {
          fields.email = user.email;
        }
      }
      if (company && company.name) {
        fields.company = company.name;
      }
      const integrationValue = this.formConfig.integrationRequest || this.formConfig.addonType;
      if (integrationValue) {
        fields.integration_request = integrationValue;
        fields.integration_requested = integrationValue;
        fields.integration = integrationValue; // Some HubSpot forms use shorter field name
      }
      const formConfig = {
        ...this.formConfig,
        fields,
        onFormReady: (form) => {
          const createTime = performance.now() - createStartTime;
          this.onFormReady(form);
          if (selectCheckbox || addonType) {
            this.selectCheckboxInForm(selectCheckbox, addonType);
          }
        },
        onFormSubmit: (form) => {
          this.onFormSubmit(form);
        },
        onFormSubmitted: (form) => {
          this.onFormSubmitted(form);
        },
        onFormError: (error) => {
          this.handleError('Error in HubSpot form', error);
        },
      };
      this.formInstance = window.hbspt.forms.create(formConfig);
    } catch (error) {
      this.handleError('Error creating HubSpot form', error);
    }
  }

  /**
   * Sets up a timeout for form loading
   */
  private setupLoadingTimeout(): void {
    // Clear any existing timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout);
    }

    // Set up a timer to update loading time elapsed every second
    const updateTimer = setInterval(() => {
      if (this.loadingStartTime > 0) {
        this.loadingTimeElapsed = Math.floor((performance.now() - this.loadingStartTime) / 1000);
      }
    }, 1000);

    // Set up timeout for form loading
    this.loadingTimeout = setTimeout(() => {
      // If form is still loading after timeout, show timeout message
      if (this.isLoading && !this.formReadyTriggered) {
        this.showTimeoutMessage = true;

        // Log the timeout to Sentry
        Sentry.addBreadcrumb({
          category: 'hubspot',
          message: 'HubSpot form loading timeout',
          level: 'warning',
          data: {
            formId: this.formConfig.formId,
            loadTime: this.loadingTimeElapsed + 's',
          },
        });
      }

      // Keep the timer running to update elapsed time
      // It will be cleared in ngOnDestroy or when form is ready
    }, this.formLoadTimeout);

    // Make sure both timers are cleaned up when component is destroyed
    this.destroy$.subscribe(() => {
      clearTimeout(this.loadingTimeout);
      clearInterval(updateTimer);
    });
  }
  /**
   * Called when the form is ready
   */
  private onFormReady(form): void {
    this.formReadyTriggered = true;
    requestAnimationFrame(() => {
      this.formIframe = this.elementRef.nativeElement.querySelector('iframe');
      if (this.formIframe && this.formIframe.contentWindow && this.formIframe.contentDocument) {
        try {
          const iframeDoc = this.formIframe.contentDocument || this.formIframe.contentWindow.document;
          const user = this.store.selectSnapshot(AuthState.user);
          const company = this.store.selectSnapshot(AuthState.company);
          if (user || company) {
            this.findAndPopulateFormFields(iframeDoc, user, company);
          }
        } catch (error) {
          Sentry.captureException(error, {
            tags: { component: 'HubspotFormDialog' },
            extra: { context: 'Setting form fields directly' },
          });
        }
      }
    });
    this.isLoading = false;
  }

  private onFormSubmit(form: any): void {
  }
  /**
   * Called when the form submission is completed
   */
  private onFormSubmitted(form: any): void {
  }
  /**
   * Handles errors that occur during form loading or creation
   */
  private handleError(message: string, error?: string): void {
    const diagnosticInfo = {
      error,
      formConfig: this.formConfig,
      scriptLoaded: this.formScriptLoaded,
      hbsptExists: typeof window.hbspt !== 'undefined',
      originalFormId: this.originalFormId,
      currentFormId: this.formConfig.formId,
      isRetrying: this.isRetrying,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString(),
    };
    Sentry.captureMessage(`HubSpot form error: ${message}`, {
      level: 'error',
      extra: diagnosticInfo,
    });
  }
  /**
   * Closes the dialog
   */
  closeDialog(): void {
    this.segmentService.trackInAppEvents('Clicked X On HubSpot Form Dialog');
    this.dialogRef.close();
  }
  /**
   * Handle touch start events to prevent scrollbar selection
   */
  @HostListener('touchstart', ['$event'])
  onTouchStart(event: TouchEvent): void {
    this.isTouchDragging = false;
  }
  /**
   * Handle touch move events to prevent scrollbar selection and show scrollbar during touch scrolling
   */
  @HostListener('touchmove', ['$event'])
  onTouchMove(event: TouchEvent): void {
    this.isTouchDragging = true;
    if (this.dialogContent) {
      this.handleDialogScroll();
    }
  }
  /**
   * Handle touch end events to prevent scrollbar selection
   */
  @HostListener('touchend', ['$event'])
  onTouchEnd(event: TouchEvent): void {
    if (this.isTouchDragging) {
      event.preventDefault();
    }
    this.isTouchDragging = false;
  }
  private findAndPopulateFormFields(iframeDoc: Document, user?: any, company?: any): void {
    if (!iframeDoc || !this.formIframe || !this.formIframe.contentWindow) return;
    const iframeWindow = this.formIframe.contentWindow;
    const formId = this.formConfig.formId || '';

    setTimeout(() => {
      // Build selectors dynamically using current formId (HubSpot uses {fieldName}-{formId} pattern)
      const selectors = {
        firstName: [
          `input[name="firstname-${formId}"]`,
          `input[id="firstname-${formId}"]`,
          'input[name^="firstname-"]',
          'input[id^="firstname-"]',
        ],
        email: [
          `input[name="email-${formId}"]`,
          `input[id="email-${formId}"]`,
          'input[name^="email-"]',
          'input[id^="email-"]',
          'input[type="email"]',
        ],
        company: [
          `input[name="company-${formId}"]`,
          `input[id="company-${formId}"]`,
          'input[name^="company-"]',
          'input[id^="company-"]',
        ],
      };

      const nameParts = user?.name?.trim()?.split(' ') || [];
      const firstName = nameParts[0] || '';
      const firstNameEl = this.findFieldBySelectors(iframeDoc, selectors.firstName, iframeWindow, 'INPUT');
      if (firstNameEl && firstName) {
        (firstNameEl as HTMLInputElement).value = firstName;
        this.dispatchInputEvent(firstNameEl as HTMLInputElement);
      }

      const emailEl = this.findFieldBySelectors(iframeDoc, selectors.email, iframeWindow, 'INPUT');
      if (emailEl && user?.email) {
        (emailEl as HTMLInputElement).value = user.email;
        this.dispatchInputEvent(emailEl as HTMLInputElement);
      }

      const companyEl = this.findFieldBySelectors(iframeDoc, selectors.company, iframeWindow, 'INPUT');
      if (companyEl && company?.name) {
        (companyEl as HTMLInputElement).value = company.name;
        this.dispatchInputEvent(companyEl as HTMLInputElement);
      }

      // Integration Requested field - use integrationRequest from config, or addonType as fallback
      const integrationValue = this.formConfig.integrationRequest || this.formConfig.addonType;
      if (integrationValue) {
        const integrationEl = this.findIntegrationRequestField(iframeDoc, formId);
        if (integrationEl) {
          this.setIntegrationFieldValue(integrationEl, integrationValue);
          this.dispatchInputEvent(integrationEl);
        }
      }
    }, 100);
  }

  /**
   * Sets the integration field value. For SELECT elements, tries value match then option text match.
   */
  private setIntegrationFieldValue(field: FormFieldElement, value: string): void {
    if (field.tagName === 'SELECT') {
      const selectEl = field as HTMLSelectElement;
      const valueLower = value.toLowerCase();
      // Try exact value match first
      for (let i = 0; i < selectEl.options.length; i++) {
        const opt = selectEl.options[i];
        if (opt.value === value || opt.value.toLowerCase() === valueLower) {
          selectEl.selectedIndex = i;
          return;
        }
        if (opt.text.toLowerCase() === valueLower || opt.text.trim().toLowerCase().includes(valueLower)) {
          selectEl.selectedIndex = i;
          return;
        }
      }
    } else {
      (field as HTMLInputElement | HTMLTextAreaElement).value = value;
    }
  }

  /**
   * Finds the Integration Requested field by common HubSpot naming patterns or by label
   */
  private findIntegrationRequestField(iframeDoc: Document, formId: string): FormFieldElement | null {
    const bySelector = this.findIntegrationFieldBySelectors(iframeDoc, formId);
    if (bySelector) return bySelector;

    return this.findIntegrationFieldByLabel(iframeDoc);
  }

  private findIntegrationFieldBySelectors(iframeDoc: Document, formId: string): FormFieldElement | null {
    const patterns = [
      `input[name="integration_request-${formId}"]`,
      `input[id="integration_request-${formId}"]`,
      `textarea[name="integration_request-${formId}"]`,
      `textarea[id="integration_request-${formId}"]`,
      `select[name="integration_request-${formId}"]`,
      `select[id="integration_request-${formId}"]`,
      'input[name*="integration_request"]',
      'textarea[name*="integration_request"]',
      'select[name*="integration_request"]',
      'input[name*="integration_requested"]',
      'textarea[name*="integration_requested"]',
      'select[name*="integration_requested"]',
      'input[name="integration"]',
      'textarea[name="integration"]',
      'select[name="integration"]',
      'input[name*="integration"]',
      'select[name*="integration"]',
    ];

    for (const selector of patterns) {
      try {
        const el = iframeDoc.querySelector(selector);
        if (el && 'value' in el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT')) {
          return el as FormFieldElement;
        }
      } catch {
        // Continue to next selector
      }
    }
    return null;
  }

  private findIntegrationFieldByLabel(iframeDoc: Document): FormFieldElement | null {
    const labels = Array.from(iframeDoc.querySelectorAll('label'));
    for (const label of labels) {
      const labelText = (label.textContent || '').toLowerCase();
      // Match "integration" with optional "request"/"interest"/"requested"/"which" etc.
      const isIntegrationField =
        labelText.includes('integration') &&
        (labelText.includes('request') ||
          labelText.includes('interest') ||
          labelText.includes('requested') ||
          labelText.includes('which') ||
          labelText === 'integration');
      if (isIntegrationField) {
        const field = this.getFieldFromLabel(iframeDoc, label);
        if (field) return field;
      }
    }
    return null;
  }

  private getFieldFromLabel(iframeDoc: Document, label: Element): FormFieldElement | null {
    const forId = label.getAttribute('for');
    if (forId) {
      try {
        const field = iframeDoc.getElementById(forId);
        if (field && 'value' in field && (field.tagName === 'INPUT' || field.tagName === 'TEXTAREA' || field.tagName === 'SELECT')) {
          return field as FormFieldElement;
        }
      } catch {
        // ID might have special chars
      }
    }
    const parent = label.closest('.hs-form-field, .hs-field, [class*="field"]');
    const inputField = parent?.querySelector('input, textarea, select');
    return inputField && 'value' in inputField ? (inputField as FormFieldElement) : null;
  }
  /**
   * Finds a form field using a prioritized list of selectors
   */
  private findFieldBySelectors(
    iframeDoc: Document,
    selectors: string[],
    iframeWindow: Window,
    tagName: 'INPUT' | 'TEXTAREA' | 'BOTH' = 'INPUT',
  ): FormFieldElement | null {
    const allowedTags = tagName === 'BOTH' ? ['INPUT', 'TEXTAREA'] : [tagName];
    for (const selector of selectors) {
      try {
        const element = iframeDoc.querySelector(selector);
        if (
          element &&
          'value' in element &&
          allowedTags.includes(element.tagName)
        ) {
          return element as FormFieldElement;
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'HubspotFormDialog' },
          extra: {
            context: 'Querying form field selector',
            selector,
          },
        });
      }
    }
    return null;
  }
  /**
   * Dispatches input, change, and blur events on an input/textarea element to simulate user input
   */
  private dispatchInputEvent(inputElement: FormFieldElement): void {
    try {
      inputElement.dispatchEvent(new Event('input', { bubbles: true }));
      inputElement.dispatchEvent(new Event('change', { bubbles: true }));
      inputElement.dispatchEvent(new Event('blur', { bubbles: true }));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: 'HubspotFormDialog' },
        extra: { context: 'Dispatching input events' },
      });
    }
  }
  /**
   * Selects a checkbox in the HubSpot form iframe based on the provided identifier or addon type
   * @param checkboxId The ID or name of the checkbox to select
   * @param addonType The type of addon that was selected
   */
  private selectCheckboxInForm(checkboxId?: string, addonType?: string): void {
    setTimeout(() => {
      try {
        const iframe = this.formIframe;
        if (!iframe || !iframe.contentWindow || !iframe.contentDocument) {
          return;
        }
        const iframeDoc = iframe.contentDocument || iframe.contentWindow.document;
        const checkboxes = iframeDoc.querySelectorAll('input[type="checkbox"]');
        if (checkboxes.length === 0) {
          return;
        }
        let checkboxToSelect: HTMLInputElement | null = null;

        const findByLabelIncludes = (needle: string) => {
          const target = (needle || '').toLowerCase();
          if (!target) return;
          checkboxes.forEach((checkbox: HTMLInputElement) => {
            const id = checkbox.id;
            const label = iframeDoc.querySelector(`label[for="${id}"]`);
            if (label && label.textContent) {
              const labelText = label.textContent.toLowerCase();
              if (labelText.includes(target)) {
                checkboxToSelect = checkbox;
              }
            }
          });
        };

        // Special cases by addonType
        if (addonType) {
          const at = addonType.toLowerCase();
          // Plan-feature upsells (feature interest form)
          if (!checkboxToSelect && at.includes('internet connectivity')) {
            findByLabelIncludes('internet connectivity');
          }
          if (!checkboxToSelect && at.includes('productivity ratings')) {
            findByLabelIncludes('productivity ratings');
          }
          if (!checkboxToSelect && (at.includes('web') && at.includes('app usage'))) {
            findByLabelIncludes('web');
            findByLabelIncludes('app usage');
          }
          if (!checkboxToSelect && at.includes('activity summary')) {
            findByLabelIncludes('activity summary');
          }
          if (!checkboxToSelect && at.includes('schedules')) {
            findByLabelIncludes('schedules');
          }
          if (!checkboxToSelect && at.includes('attendance')) {
            findByLabelIncludes('attendance');
          }
          if (!checkboxToSelect && at === 'breaks') {
            findByLabelIncludes('breaks');
          }
          // BigQuery
          if (at.includes('big query') || at.includes('bigquery')) {
            findByLabelIncludes('big query');
            findByLabelIncludes('bigquery');
          }
          // Benchmarks
          if (!checkboxToSelect && (at.includes('benchmark') || at === 'benchmarks')) {
            // Match both exact "benchmarks" and label that contains benchmark
            findByLabelIncludes('benchmarks');
            if (!checkboxToSelect) findByLabelIncludes('benchmark');
          }
          // Video Screen Recording
          if (!checkboxToSelect && (at.includes('video') || at.includes('screen recording'))) {
            // Require both words present if possible
            checkboxes.forEach((checkbox: HTMLInputElement) => {
              const id = checkbox.id;
              const label = iframeDoc.querySelector(`label[for="${id}"]`);
              if (label && label.textContent) {
                const labelText = label.textContent.toLowerCase();
                if (labelText.includes('video') && labelText.includes('screen recording')) {
                  checkboxToSelect = checkbox;
                }
              }
            });
          }
          // Unusual Activity (UAR)
          if (!checkboxToSelect && (at === 'uar' || at.includes('unusual activity') || at.includes('unusual activity report'))) {
            findByLabelIncludes('unusual activity');
          }
          // Office vs Remote (OVR)
          if (!checkboxToSelect && (at.includes('office') || at.includes('remote') || at.includes('office-vs-remote'))) {
            // Try common variants
            findByLabelIncludes('office vs remote');
            if (!checkboxToSelect) findByLabelIncludes('office vs. remote');
            if (!checkboxToSelect) findByLabelIncludes('office versus remote');
            if (!checkboxToSelect) {
              // As a last attempt, match "office" and "remote" both present
              checkboxes.forEach((checkbox: HTMLInputElement) => {
                const id = checkbox.id;
                const label = iframeDoc.querySelector(`label[for="${id}"]`);
                if (label && label.textContent) {
                  const lt = label.textContent.toLowerCase();
                  if (lt.includes('office') && lt.includes('remote')) {
                    checkboxToSelect = checkbox;
                  }
                }
              });
            }
          }
          // Software Cost Insights (SCI)
          if (!checkboxToSelect && (at.includes('software-cost-insights') || at === 'sci' || (at.includes('software') && at.includes('cost') && at.includes('insights')))) {
            // Try common variants - HubSpot label likely "Software Cost Insights"
            findByLabelIncludes('software cost insights');
            if (!checkboxToSelect) findByLabelIncludes('software-cost-insights');
            if (!checkboxToSelect) findByLabelIncludes('sci');
            if (!checkboxToSelect) {
              // Match "software", "cost", and "insights" all present
              checkboxes.forEach((checkbox: HTMLInputElement) => {
                const id = checkbox.id;
                const label = iframeDoc.querySelector(`label[for="${id}"]`);
                if (label && label.textContent) {
                  const lt = label.textContent.toLowerCase();
                  if (lt.includes('software') && lt.includes('cost') && lt.includes('insights')) {
                    checkboxToSelect = checkbox;
                  }
                }
              });
            }
          }
        }

        // Try matching by explicit checkboxId against id/name/data-name
        if (!checkboxToSelect && checkboxId) {
          const cid = checkboxId;
          checkboxToSelect = iframeDoc.querySelector(`input[type="checkbox"][id="${cid}"]`) ||
                             iframeDoc.querySelector(`input[type="checkbox"][name="${cid}"]`) ||
                             iframeDoc.querySelector(`input[type="checkbox"][data-name="${cid}"]`);
          // If still not found, try matching label text using a normalized version of checkboxId (e.g., office-vs-remote)
          if (!checkboxToSelect) {
            const normalized = (cid || '').toLowerCase().replace(/[^a-z]+/g, ' ').trim();
            if (normalized) {
              findByLabelIncludes(normalized);
            }
          }
        }

        // Generic fallback: match addonType anywhere in label
        if (!checkboxToSelect && addonType) {
          const addonTypeLower = addonType.toLowerCase();
          findByLabelIncludes(addonTypeLower);
        }

        // Final fallback: first checkbox
        if (!checkboxToSelect && checkboxes.length > 0) {
          checkboxToSelect = checkboxes[0] as HTMLInputElement;
        }

        // Set the initial checkbox state
        if (checkboxToSelect) {
          checkboxToSelect.checked = true;
          // Fire input/change events so HubSpot records the value (otherwise the UI can look checked but the value is missing)
          this.dispatchInputEvent(checkboxToSelect);

          // Create a MutationObserver to monitor changes to the checkbox state
          // This ensures our selected checkbox stays checked even if other checkboxes are clicked
          const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
              if (mutation.type === 'attributes' &&
                  mutation.attributeName === 'checked' &&
                  !checkboxToSelect.checked) {
                // If our checkbox gets unchecked, check it again
                checkboxToSelect.checked = true;
                this.dispatchInputEvent(checkboxToSelect);
              }
            });
          });

          // Also add a click event listener to all other checkboxes to ensure our selection remains
          checkboxes.forEach((checkbox: HTMLInputElement) => {
            if (checkbox !== checkboxToSelect) {
              checkbox.addEventListener('change', () => {
                // Make sure our checkbox is still checked after other checkboxes change
                if (!checkboxToSelect.checked) {
                  checkboxToSelect.checked = true;
                  this.dispatchInputEvent(checkboxToSelect);
                }
              });
            }
          });

          // Start observing the checkbox for attribute changes
          observer.observe(checkboxToSelect, {
            attributes: true,
            attributeFilter: ['checked'],
          });

          // Store the observer in the destroy$ subject to clean it up when the component is destroyed
          this.destroy$.subscribe(() => {
            observer.disconnect();
          });
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: 'HubspotFormDialog' },
          extra: {
            context: 'Selecting checkbox in HubSpot form',
            checkboxId,
            addonType,
          },
        });
      }
    }, 100);
  }
}
